import { type EmployeeLevel } from "@prisma/client";
import { value } from "~/components/helpers";
import { type AppContext } from "~/lib/context";
import { chain, compact, uniqBy } from "~/lib/lodash";
import { type Input } from "~/pages/api/get-market-data-stats";
import { type NullableAuthenticatedUser } from "~/services/auth/fetchAuthenticatedUser";
import { fetchBenchmarkLevelsRange } from "~/services/benchmark-level/fetchBenchmarkLevelsRange";
import { getDefaultCurrency, getSuggestedCurrencies } from "~/services/currency";
import { getAllowedLevels, getMergedLevels, MA0X_LEVELS } from "~/services/employee/employeeLevel";
import { resolveWeightedJobs } from "~/services/job";
import { generateEmployeesWithFiltersOptions } from "~/services/market-data/generateEmployeesWithFiltersOptions";
import {
  COMPANY_ID_FOR_ANONYMISED_DATA_IN_PRODUCTION,
  COMPANY_NAME_FOR_ANONYMISED_DATA_IN_TEST,
} from "~/services/market-data/getMarketDataStats";
import { validateJobsAuthorised } from "~/services/market-data/validateJobsAuthorised";

export const formatMarketDataInput = async (
  ctx: AppContext,
  { input, user }: { input: Input; user: NullableAuthenticatedUser }
) => {
  const company = await value(async () => {
    if (input.useTestData) {
      return ctx.prisma.company.findFirstOrThrow({
        where: {
          OR: [
            { id: COMPANY_ID_FOR_ANONYMISED_DATA_IN_PRODUCTION },
            { name: COMPANY_NAME_FOR_ANONYMISED_DATA_IN_TEST },
          ],
        },
      });
    }

    return user?.company ?? null;
  });

  //CURRENCIES - included suggested currencies for both market and compare
  const { currency, suggestedCurrencies } = await fetchCurrencies(ctx, input);

  //HANDLING LEVELS
  //to be removed when full live on Level Frameworks
  const allowedLevelsOnUser = getAllowedLevels(user, input.mergeAdvancedLevels);
  let levelOptions = chain(input.levels)
    .flatMap((level) => getMergedLevels(level, { mergeAdvancedLevels: input.mergeAdvancedLevels }))
    .filter((level) => level !== "MA0X")
    .uniq()
    .value();

  if (input.levels.includes("MA0X")) {
    levelOptions = compact([...levelOptions, ...MA0X_LEVELS]);
  }

  const allowedLevels = levelOptions.filter((level) => allowedLevelsOnUser.includes(level));

  const marketDataLevels = input.levels.filter((level) => allowedLevelsOnUser.includes(level));

  const benchmarkLevelsRange = await fetchBenchmarkLevelsRange(ctx, {
    benchmarkLevelIds: input.benchmarkLevelIds,
    useBenchmarkLevelsInShare: input.useBenchmarkLevelsInShare ?? false,
  });

  const marketWeightedJobs = await resolveWeightedJobs(ctx, input.market, {
    useTestData: input.useTestData,
  });
  const marketJobsIds = marketWeightedJobs.map((item) => item.job.id);

  const employeesWithFiltersOptions = generateEmployeesWithFiltersOptions({
    mergeAdvancedLevels: input.mergeAdvancedLevels,
    levels: levelOptions,
    benchmarkLevelsRange,
    useTestData: input.useTestData,
    marketOrCompare: input.market,
    jobIds: marketJobsIds,
    options: { canAccessFiguresAiV2: ctx.featureFlags.CAN_ACCESS_FIGURES_AI_V2 },
  });

  await validateJobsAuthorised(ctx, marketJobsIds);

  //We're fetching the industry tag for the fallback on predictive data (Figures AI is taking full company tags and not only ids.)
  const marketIndustryTag = input.market.industryId
    ? await ctx.prisma.companyTag.findUnique({ where: { id: input.market.industryId } })
    : null;

  let compareParams;

  if (input.compare) {
    compareParams = await formatCompareParams(ctx, {
      input,
      levelOptions,
      benchmarkLevelsRange,
    });
  }

  return {
    company,
    currency,
    suggestedCurrencies,
    allowedLevels,
    marketDataLevels,
    benchmarkLevelsRange,
    marketWeightedJobs,
    marketJobsIds,
    employeesWithFiltersOptions,
    marketIndustryTag,
    compareParams,
  };
};

const fetchCurrencies = async (ctx: AppContext, input: Input) => {
  const [currency, suggestedCurrenciesForLocations] = await Promise.all([
    input.currency
      ? ctx.prisma.currency.findUniqueOrThrow({ where: { code: input.currency } })
      : getDefaultCurrency(ctx),
    getSuggestedCurrencies(ctx, [...input.market.locationsIds, ...(input.compare?.locationsIds ?? [])]),
  ]);

  const suggestedCurrencies = uniqBy([currency, ...suggestedCurrenciesForLocations], (currency) => currency.code);

  return { currency, suggestedCurrencies };
};

const formatCompareParams = async (
  ctx: AppContext,
  {
    input,
    levelOptions,
    benchmarkLevelsRange,
  }: { input: Input; levelOptions: EmployeeLevel[]; benchmarkLevelsRange: { min: number; max: number } }
) => {
  if (!input.compare) {
    throw new Error("'Input' Compare is required for compare feature");
  }

  const compareWeightedJobs = await resolveWeightedJobs(ctx, input.compare, {
    useTestData: input.useTestData,
  });

  const compareJobsIds = compareWeightedJobs.map((item) => item.job.id);

  await validateJobsAuthorised(ctx, compareJobsIds);

  const comparedEmployeesWithFiltersOptions = generateEmployeesWithFiltersOptions({
    mergeAdvancedLevels: input.mergeAdvancedLevels,
    levels: levelOptions,
    benchmarkLevelsRange,
    useTestData: input.useTestData,
    marketOrCompare: input.compare,
    jobIds: compareJobsIds,
    options: { canAccessFiguresAiV2: ctx.featureFlags.CAN_ACCESS_FIGURES_AI_V2 },
  });

  const comparedMarketIndustryTag = input.market.industryId
    ? await ctx.prisma.companyTag.findUnique({ where: { id: input.market.industryId } })
    : null;

  return {
    compareWeightedJobs,
    compareJobsIds,
    comparedEmployeesWithFiltersOptions,
    comparedMarketIndustryTag,
  };
};
